import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Network, ClipboardList, Pill, Home as HomeIcon, FileBarChart,
  Stethoscope, Landmark, ArrowRight, AlertCircle,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import { FAQSection, FAQS } from "../components/FAQ";

const BLOCKS = [
  {
    icon: Network, color: "#10B981", title: "ARRS clinical pharmacy workforce",
    text: "Where your PCN is eligible, NCS can support ARRS-funded clinical pharmacy roles — clinical pharmacists and pharmacy technicians who integrate with your existing team, with clear supervision and governance arrangements.",
  },
  {
    icon: ClipboardList, color: "#06B6D4", title: "PCN DES clinical service priorities",
    text: "Delivery aligned to the Network Contract DES — supporting the clinical service priorities your network is working towards, with activity reporting that evidences delivery.",
  },
  {
    icon: Pill, color: "#F59E0B", title: "Structured Medication Reviews",
    text: "Pharmacist-led SMRs for patients with complex polypharmacy, frailty and high-risk prescribing — a core PCN DES service and one of our most-requested commissions.",
  },
  {
    icon: HomeIcon, color: "#EF4444", title: "Enhanced Health in Care Homes",
    text: "Clinical pharmacy support for care home residents, aligned to the Enhanced Health in Care Homes framework — medication reviews, medicines optimisation and multidisciplinary working.",
  },
  {
    icon: FileBarChart, color: "#10B981", title: "Medicines optimisation & prescribing quality",
    text: "Prescribing audits, formulary adherence, safety programmes (including opioid and gabapentinoid reviews) and targeted prescribing improvement projects.",
  },
  {
    icon: Stethoscope, color: "#06B6D4", title: "GP practice clinical capacity",
    text: "Additional pharmacist and technician capacity for individual practices — from sessional cover to long-term placements that release GP time.",
  },
  {
    icon: Landmark, color: "#F59E0B", title: "ICB-commissioned clinical projects",
    text: "Defined clinical projects commissioned at ICB level — scoped, delivered and reported with the governance commissioners expect.",
  },
];

const ArrsSupport = () => (
  <main data-testid="arrs-page">
      <Seo
        title="ARRS, PCN DES & NHS Clinical Services Support | NCS"
        description="How NCS supports ARRS clinical pharmacy roles, PCN DES priorities, care homes, medicines optimisation and ICB-commissioned clinical projects."
        faq={FAQS}
      />
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">ARRS, PCN DES & NHS services</p>
        <h1
          data-testid="arrs-heading"
          className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl max-w-4xl"
        >
          <MaskedLine delay={0.15}>Aligned to How the NHS</MaskedLine>
          <MaskedLine delay={0.3}><span className="font-serif-accent">Actually</span> Commissions Care</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl"
        >
          NCS works within the frameworks primary care already uses — the Additional Roles
          Reimbursement Scheme, the Network Contract DES, enhanced services and ICB commissioning —
          so support lands where your funding and priorities already sit.
        </motion.p>
      </div>
    </section>

    <section className="pb-16 sm:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {BLOCKS.map((b, i) => (
            <Reveal key={b.title} delay={(i % 2) * 0.08}>
              <div
                data-testid={`arrs-block-${i + 1}`}
                className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
              >
                <span
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${b.color}1A`, color: b.color }}
                >
                  <b.icon size={22} />
                </span>
                <h2 className="mt-6 font-display text-xl font-bold text-[#0B192C]">{b.title}</h2>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#475569]">{b.text}</p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="h-full bg-[#0B192C] rounded-3xl p-8 flex flex-col" data-testid="arrs-distinction-card">
              <AlertCircle size={24} className="text-[#F59E0B]" />
              <h2 className="mt-6 font-display text-xl font-bold text-white">How the Funding Fits Together</h2>
              <ul className="mt-4 space-y-3.5 text-sm leading-relaxed text-white/65 flex-1">
                <li><strong className="text-white">ARRS-funded roles</strong> — workforce reimbursed through the Additional Roles Reimbursement Scheme, where your PCN is eligible and within scheme rules.</li>
                <li><strong className="text-white">PCN DES requirements</strong> — service obligations under the Network Contract DES (such as SMRs and care homes) that our delivery can support.</li>
                <li><strong className="text-white">Separately commissioned services</strong> — clinical projects commissioned directly by PCNs, practices, federations or ICBs outside ARRS.</li>
              </ul>
              <p className="mt-5 text-xs leading-relaxed text-white/45">
                NCS does not guarantee ARRS reimbursement, and not every service qualifies for ARRS
                funding. Eligibility always sits with NHS England rules and your ICB — we will help
                you structure support appropriately.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <FAQSection />

    <section className="py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl mx-auto">
            Ready to Structure Support Around Your Funding?
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="arrs-cta-button"
              className="group inline-flex items-center gap-2.5 bg-[#0B192C] text-white font-bold px-8 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C]"
            >
              Speak to Our Clinical Services Team
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/case-study"
              data-testid="arrs-case-study-link"
              className="inline-flex items-center gap-2.5 bg-white text-[#0B192C] font-bold px-8 py-4 rounded-full text-sm border border-[#E2E8F0] transition-colors duration-300 hover:border-[#0B192C]"
            >
              Read the Case Study
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default ArrsSupport;
